import React, { useEffect, useMemo } from 'react';
import * as THREE from 'three';
import {
  CoveringLayer,
  resolveWallSides,
  resolveSideStyle,
  CORE_COLOR,
  FACE_OFFSET_CM,
  BASEBOARD_DEPTH_CM,
  DEFAULT_BASEBOARD_COLOR,
  DEFAULT_BASEBOARD_HEIGHT_CM,
  WALL_SHADE_USERDATA,
} from './Wall3D.jsx';
import { AO, getGradientTexture } from './ambientOcclusion.jsx';

// دیوارِ منحنی در سه‌بعدی: «یک» سطحِ پیوسته با نرمالِ نرم برای هر رویه (نه چند جعبه‌ی صافِ پشتِ‌سرِهم که پله‌پله و
// تکه‌تکه دیده می‌شد). همان پوشش‌ها و جنس‌های دیوارِ معمولی (رنگ، کاشی، سنگ، پوششِ پایینی، قرنیز، سایه‌ی کنج).
// subWalls: تکه‌های همان دیوار به ترتیب (expandArcs) — نقطه‌های قوس از سرهای آن‌ها می‌آید.

const noRaycast = () => null;

// نوارِ عمودی در امتدادِ قوس: نقطه‌ها pts، عمودها nrm، فاصله‌ی off از محور، از ارتفاعِ y0 تا y1. uv: u = طول (۰..۱)
function ribbon(pts, nrm, off, y0, y1, facing) {
  const pos = [];
  const nor = [];
  const uv = [];
  const idx = [];
  const lens = [0];
  for (let i = 1; i < pts.length; i += 1) lens.push(lens[i - 1] + Math.hypot(pts[i][0] - pts[i - 1][0], pts[i][1] - pts[i - 1][1]));
  const total = lens[lens.length - 1] || 1;
  pts.forEach(([x, z], i) => {
    const nx = nrm[i][0];
    const nz = nrm[i][1];
    const px = x + nx * off;
    const pz = z + nz * off;
    // رویه‌ی b (facing −۱) از آن طرف دیده می‌شود: u برعکس تا بافت آینه‌ای نشود
    const u = facing > 0 ? lens[i] / total : 1 - lens[i] / total;
    pos.push(px, y0, pz, px, y1, pz);
    nor.push(nx * facing, 0, nz * facing, nx * facing, 0, nz * facing);
    uv.push(u, 0, u, 1);
  });
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = i * 2;
    const b = a + 2;
    if (facing > 0) idx.push(a, b, b + 1, a, b + 1, a + 1);
    else idx.push(a, a + 1, b + 1, a, b + 1, b);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setAttribute('normal', new THREE.Float32BufferAttribute(nor, 3));
  g.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv1', new THREE.Float32BufferAttribute(uv, 2));
  g.setAttribute('uv2', new THREE.Float32BufferAttribute(uv, 2));
  g.setIndex(idx);
  return { geometry: g, length: total };
}

// سطحِ افقیِ بینِ دو فاصله (بالای دیوار، روی قرنیز)
function band(pts, nrm, off0, off1, y, up) {
  const pos = [];
  const idx = [];
  pts.forEach(([x, z], i) => {
    pos.push(x + nrm[i][0] * off0, y, z + nrm[i][1] * off0, x + nrm[i][0] * off1, y, z + nrm[i][1] * off1);
  });
  for (let i = 0; i < pts.length - 1; i += 1) {
    const a = i * 2;
    const b = a + 2;
    idx.push(a, b, a + 1, a + 1, b, b + 1);
  }
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  if (up !== undefined) {
    const n = g.getAttribute('normal');
    for (let i = 0; i < n.count; i += 1) n.setXYZ(i, 0, up ? 1 : -1, 0);
  }
  return g;
}

function useDispose(list) {
  useEffect(() => () => list.forEach((g) => g && g.dispose && g.dispose()), [list]);
}

function CurvedFace({ side, pts, nrm, facing, thickness, height, onSelect, aoTop }) {
  const s = resolveSideStyle(side);
  const off = facing * (thickness / 2 + FACE_OFFSET_CM);
  const splitLine = s.lower ? Math.min(height, Math.max(0, s.lower.heightCm)) : 0;
  const geos = useMemo(() => {
    const lower = splitLine > 0 ? ribbon(pts, nrm, off, 0, splitLine, facing) : null;
    const main = ribbon(pts, nrm, off, splitLine, height, facing);
    const bbH = Math.max(1, s.baseboard?.height || DEFAULT_BASEBOARD_HEIGHT_CM);
    const offB = facing * (thickness / 2 + FACE_OFFSET_CM + BASEBOARD_DEPTH_CM);
    const bbFront = s.baseboard?.enabled ? ribbon(pts, nrm, offB, 0, bbH, facing).geometry : null;
    const bbTop = s.baseboard?.enabled ? band(pts, nrm, off, offB, bbH, true) : null;
    const offAo = facing * (thickness / 2 + FACE_OFFSET_CM + 0.25);
    const aoB = ribbon(pts, nrm, offAo, 0, AO.wallBottomHeight, facing).geometry;
    const aoT = aoTop ? ribbon(pts, nrm, offAo, height - AO.wallTopHeight, height, facing).geometry : null;
    if (aoT) {
      // تیرگی بالا (کنارِ سقف): v برعکس
      const uv = aoT.getAttribute('uv');
      for (let i = 0; i < uv.count; i += 1) uv.setY(i, 1 - uv.getY(i));
    }
    return { lower, main, bbFront, bbTop, aoB, aoT };
  }, [pts, nrm, off, splitLine, height, facing, thickness, s.baseboard?.enabled, s.baseboard?.height, aoTop]);
  useDispose(useMemo(() => [geos.lower && geos.lower.geometry, geos.main.geometry, geos.bbFront, geos.bbTop, geos.aoB, geos.aoT], [geos]));

  const handleClick = (e) => {
    e.stopPropagation();
    if (onSelect) onSelect(facing > 0 ? 'a' : 'b');
  };
  const aoMat = (opacity) => (
    <meshBasicMaterial
      color="#15110c"
      alphaMap={getGradientTexture()}
      transparent
      opacity={opacity}
      depthWrite={false}
      polygonOffset
      polygonOffsetFactor={-2}
      polygonOffsetUnits={-2}
      side={THREE.DoubleSide}
      userData={{ khNoRoomLight: true }}
    />
  );
  return (
    <group onClick={handleClick}>
      {geos.lower && (
        <CoveringLayer
          layer={s.lower}
          length={geos.lower.length}
          height={splitLine}
          alongStart={0}
          vertStart={0}
          finish={s.finish}
          geometry={geos.lower.geometry}
        />
      )}
      <CoveringLayer
        layer={s.main}
        length={geos.main.length}
        height={height - splitLine}
        alongStart={0}
        vertStart={splitLine}
        finish={s.finish}
        geometry={geos.main.geometry}
      />
      <mesh geometry={geos.aoB} raycast={noRaycast} renderOrder={2}>
        {aoMat(AO.wallBottomOpacity)}
      </mesh>
      {geos.aoT && (
        <mesh geometry={geos.aoT} raycast={noRaycast} renderOrder={2}>
          {aoMat(AO.wallTopOpacity)}
        </mesh>
      )}
      {geos.bbFront && (
        <>
          <mesh geometry={geos.bbFront}>
            <meshStandardMaterial color={s.baseboard.color || DEFAULT_BASEBOARD_COLOR} roughness={0.6} metalness={0} envMapIntensity={0.3} />
          </mesh>
          <mesh geometry={geos.bbTop}>
            <meshStandardMaterial color={s.baseboard.color || DEFAULT_BASEBOARD_COLOR} roughness={0.6} metalness={0} envMapIntensity={0.3} />
          </mesh>
        </>
      )}
    </group>
  );
}

function CurvedWall3D({ subWalls, thickness, height, onSelectWall, highlightSide, interiorAo }) {
  const wall = subWalls[0];
  // نقطه‌های قوس + عمودِ نرم در هر نقطه (میانگینِ دو تکه‌ی کنارش) — همین «نرم» بودن است که سطح را یک‌دست می‌کند
  const { pts, nrm } = useMemo(() => {
    const P = [[subWalls[0].x1, subWalls[0].y1], ...subWalls.map((w) => [w.x2, w.y2])];
    const segN = subWalls.map((w) => {
      const L = Math.hypot(w.x2 - w.x1, w.y2 - w.y1) || 1;
      return [-(w.y2 - w.y1) / L, (w.x2 - w.x1) / L];
    });
    const N = P.map((_, i) => {
      const a = segN[Math.max(0, i - 1)];
      const b = segN[Math.min(segN.length - 1, i)];
      const x = a[0] + b[0];
      const z = a[1] + b[1];
      const L = Math.hypot(x, z) || 1;
      // عمودِ میانگین کمی کوتاه‌تر از ۱ است؛ برای ضخامتِ دقیق در همه‌جا تقسیم بر کسینوسِ نیم‌زاویه
      const cos = (a[0] * b[0] + a[1] * b[1] + 1) / 2;
      const k = 1 / Math.sqrt(Math.max(0.25, cos));
      return [(x / L) * k, (z / L) * k];
    });
    return { pts: P, nrm: N };
  }, [subWalls]);

  const sides = resolveWallSides(wall);
  const t2 = thickness / 2;
  const core = useMemo(() => {
    const top = band(pts, nrm, -t2, t2, height, true);
    // سرهای دیوار (دو مستطیلِ عمودی)
    const cap = (i, sign) => {
      const [x, z] = pts[i];
      const [nx, nz] = nrm[i];
      const g = new THREE.BufferGeometry();
      g.setAttribute(
        'position',
        new THREE.Float32BufferAttribute([x - nx * t2, 0, z - nz * t2, x + nx * t2, 0, z + nz * t2, x + nx * t2, height, z + nz * t2, x - nx * t2, height, z - nz * t2], 3)
      );
      g.setIndex(sign > 0 ? [0, 1, 2, 0, 2, 3] : [0, 2, 1, 0, 3, 2]);
      g.computeVertexNormals();
      return g;
    };
    return [top, cap(0, -1), cap(pts.length - 1, 1)];
  }, [pts, nrm, t2, height]);
  useDispose(core);
  const hl = useMemo(() => (highlightSide ? band(pts, nrm, (highlightSide === 'a' ? 1 : -1) * (t2 + 2), (highlightSide === 'a' ? 1 : -1) * (t2 + 5), 1.5, true) : null), [highlightSide, pts, nrm, t2]);
  useDispose(useMemo(() => [hl], [hl]));

  const onSelect = (side) => onSelectWall && onSelectWall(wall.id, side);
  return (
    <group>
      {core.map((g, i) => (
        <mesh key={i} geometry={g}>
          <meshStandardMaterial color={CORE_COLOR} roughness={0.95} metalness={0} envMapIntensity={0.4} side={THREE.DoubleSide} userData={WALL_SHADE_USERDATA} />
        </mesh>
      ))}
      <CurvedFace side={sides?.a} pts={pts} nrm={nrm} facing={1} thickness={thickness} height={height} onSelect={onSelect} aoTop={interiorAo} />
      <CurvedFace side={sides?.b} pts={pts} nrm={nrm} facing={-1} thickness={thickness} height={height} onSelect={onSelect} aoTop={interiorAo} />
      {hl && (
        <mesh geometry={hl} raycast={noRaycast}>
          <meshBasicMaterial color="#0a66c2" side={THREE.DoubleSide} />
        </mesh>
      )}
    </group>
  );
}

export default CurvedWall3D;
